import React from 'react';
import isEqual from 'lodash/isEqual';
import {
  DndContext,
  useSensor,
  closestCenter,
  useSensors,
  TouchSensor,
  MouseSensor,
  MeasuringStrategy,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import BodyRowDraggable, { BodyRowDraggablePure } from './BodyRowDraggable';
import BodyWrapper from '../BodyWrapper';
import { IColumn, IRowData } from '../../interfaces';
import { getUniqueIdentifier } from '../../helpers';

interface IProps {
  data?: IRowData[],
  orderCallback?: (oldIndex: number, newIndex: number) => void,
  isUsePureUpdateComponent?: boolean,
  columns: IColumn[],
  rowCallbacks?: {
    [key: string]: (event: Event, rowData: IRowData) => void,
  },
  rowClassNameCallback?: (rowData: IRowData) => string,
  selectedRows?: IRowData[],
  isAnimation?: boolean,
  rows?: React.ReactNode,
  draggingClassName?: string,
  topNeighborClassName?: string,
  bottomNeighborClassName?: string,
  rowKey?: string | ((rowData: IRowData, index: number) => string),
}

const BodyDraggable = ({
  data,
  orderCallback,
  isUsePureUpdateComponent,
  columns,
  rowCallbacks,
  rowClassNameCallback,
  selectedRows,
  isAnimation,
  rows,
  rowKey,
  draggingClassName,
  topNeighborClassName,
  bottomNeighborClassName,
}: IProps) => {
  const sensors = useSensors(
    useSensor(TouchSensor),
    useSensor(MouseSensor),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active?.id !== over?.id) {
      const oldIndex = Number(active.id);
      const newIndex = Number(over.id);
      orderCallback(oldIndex, newIndex);
    }
  };

  const SortableRowComponent = isUsePureUpdateComponent ? BodyRowDraggablePure : BodyRowDraggable;

  return (
    data.length ? (
      <DndContext
        modifiers={ [restrictToParentElement] }
        onDragEnd={ onDragEnd }
        collisionDetection={ closestCenter }
        sensors={ sensors }
        measuring={ {
          droppable: {
            strategy: isAnimation ? MeasuringStrategy.Always : MeasuringStrategy.WhileDragging,
          },
        } }
      >
        <BodyWrapper>
          <SortableContext
            items={ data.map((_, index) => getUniqueIdentifier(index)) }
            strategy={ verticalListSortingStrategy }
          >
            { rows || data.map((rowData, index) => {
              const key = (typeof rowKey === 'function' ? rowKey(rowData, index) : rowData[rowKey]) ?? index;

              return (
                <SortableRowComponent
                  key={ key }
                  rowIndex={ index }
                  uniqueIdentifier={ getUniqueIdentifier(index) }
                  rowData={ rowData }
                  isLastChild={ data.length - 1 === index }
                  rowCallbacks={ rowCallbacks }
                  rowClassNameCallback={ rowClassNameCallback }
                  columns={ columns }
                  isSelected={ selectedRows?.some(isEqual.bind(this, rowData)) }
                  isAnimation={ isAnimation }
                  className={ rowData.className }
                  isDragAndDropDisabled={ rowData.isDragAndDropDisabled }
                  draggingClassName={ draggingClassName }
                  topNeighborClassName={ topNeighborClassName }
                  bottomNeighborClassName={ bottomNeighborClassName }
                />
              );
            }) }
          </SortableContext>
        </BodyWrapper>
      </DndContext>
    ) : null
  );
};

export default BodyDraggable;
